import React, { useMemo } from "react";
import { Lock } from "@mui/icons-material";
import { Box } from "@mui/material";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useGetImportLayers } from "fond/hooks/useGetImportLayers";
import { ISGLayerIds, isImportInProgress, Project, WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AccordionStepper, AccordionStepperPane, NonIdealState, StackedNavigation } from "fond/widgets";

import BlockersDrawPanel from "./DesignPanel/BlockersDrawPanel";
import DesignPanel from "./DesignPanel/DesignPanel";
import BoundaryImport from "./InputPanel/Boundary/BoundaryImport";
import BoundaryPanel from "./InputPanel/Boundary/BoundaryPanel";
import BoundaryUpload from "./InputPanel/Boundary/BoundaryUpload";
import DataPrepDraw from "./InputPanel/DataPrepDraw";
import LayerDraw from "./InputPanel/LayerDraw";
import LayerExport from "./InputPanel/LayerExport";
import LayerPanel from "./InputPanel/LayerPanel";
import { isgInputLayers } from "./InputPanel/layers";
import LayerUploadWrapper from "./InputPanel/LayerUploadWrapper";
import DataPrepPanel from "./DataPrepPanel";
import { InputPanel } from "./InputPanel";

interface PanelProps {
  project: Project;
  readOnly: boolean;
}

const Panel: React.FC<PanelProps> = ({ project, readOnly }: PanelProps) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const inputLayers = useGetImportLayers(versionId, Object.values(ISGLayerIds));
  const { workflowState: dataPrepWorkflow } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.ISG.DATA_PREP });
  const { workflowState: solveWorkflow } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.ISG.SOLVE });

  const hasInProgressLayers = useMemo(() => {
    return Array.from(inputLayers.values()).some((layer) => layer.status && isImportInProgress(layer.status));
  }, [inputLayers]);

  const hasBoundary = !!inputLayers.get(ISGLayerIds.boundary)?.layer;
  const hasAddress = !!inputLayers.get(ISGLayerIds.address)?.layer;
  const allRequiredLayersUploaded = hasBoundary && hasAddress;
  const isDataPrepInProgress = dataPrepWorkflow === "STARTING" || dataPrepWorkflow === "IN_PROGRESS";
  const isSolveInProgress = solveWorkflow === "STARTING" || solveWorkflow === "IN_PROGRESS";

  const isgStackedNavigationPanels: Array<{ id: string; name: string; component: React.ReactNode; useDefaultHeader?: boolean }> = useMemo(() => {
    const panels: Array<{ id: string; name: string; component: React.ReactNode; useDefaultHeader?: boolean }> = [];

    // Check if any layers that will be imported already have data
    const hasImportableLayerData =
      !!inputLayers.get(ISGLayerIds.address)?.layer ||
      !!inputLayers.get(ISGLayerIds.parcels)?.layer ||
      !!inputLayers.get(ISGLayerIds.streets)?.layer ||
      !!inputLayers.get(ISGLayerIds.waterways)?.layer ||
      !!inputLayers.get(ISGLayerIds.railways)?.layer;

    // Add boundary panels (special case with import functionality)
    panels.push(
      {
        id: ISGLayerIds.boundary,
        name: "Boundary",
        component: <BoundaryPanel hasBoundary={hasBoundary} />,
        useDefaultHeader: false,
      },
      {
        id: "boundary-upload",
        name: "Upload",
        component: <BoundaryUpload />,
        useDefaultHeader: false,
      },
      {
        id: "boundary-import",
        name: "Import",
        component: <BoundaryImport hasExistingData={hasImportableLayerData} />,
        useDefaultHeader: false,
      }
    );

    // Generate panels for all layers from configuration
    isgInputLayers.forEach((layerConfig) => {
      const { key, displayName, navigationKey, geometryType, drawingConfig, filterFeatures, combineToMultiPolygon } = layerConfig;
      const hasExistingData = !!inputLayers.get(key)?.layer;

      // Add draw screen
      panels.push({
        id: `${navigationKey}-draw`,
        name: "Draw on map",
        component: (
          <LayerDraw
            versionId={versionId}
            layerId={key}
            displayName={displayName}
            drawingConfig={drawingConfig}
            filterFeatures={filterFeatures}
            combineToMultiPolygon={combineToMultiPolygon}
          />
        ),
      });

      // Add export screen
      panels.push({
        id: `${navigationKey}-export`,
        name: "Export",
        component: <LayerExport layerId={key} displayName={displayName} title={`Export ${displayName.toLowerCase()}`} hasData={hasExistingData} />,
        useDefaultHeader: false,
      });

      if (key !== ISGLayerIds.boundary) {
        // Add upload screen
        panels.push({
          id: `${navigationKey}-upload`,
          name: "Upload",
          component: (
            <LayerUploadWrapper
              layerId={key}
              displayName={displayName}
              title={`Upload ${displayName.toLowerCase()}`}
              geometryType={geometryType}
              hasExistingData={hasExistingData}
            />
          ),
          useDefaultHeader: false,
        });

        // Add panel screen
        panels.push({
          id: key,
          name: displayName,
          component: <LayerPanel title={displayName} layerKey={navigationKey} />,
          useDefaultHeader: false,
        });
      }
    });

    // Add data prep draw screen
    panels.push({
      id: "data-prep-draw",
      name: "Modify data",
      component: <DataPrepDraw />,
      useDefaultHeader: false,
    });

    panels.push({
      id: "design-blockers-draw",
      name: "Draw blockers",
      component: <BlockersDrawPanel />,
      useDefaultHeader: false,
    });

    return panels;
  }, [hasBoundary, versionId, inputLayers]);

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Input data",
        id: "input",
        content: <InputPanel isReadOnly={readOnly} inputLayers={inputLayers} />,
        isComplete: allRequiredLayersUploaded && !hasInProgressLayers,
        isLoading: hasInProgressLayers,
      },
      {
        header: "Data preparation",
        id: "dataPrep",
        content: <DataPrepPanel isReadOnly={readOnly} canRun={allRequiredLayersUploaded} />,
        isComplete: dataPrepWorkflow === "COMPLETE",
        isLoading: isDataPrepInProgress,
      },
      {
        header: "Design",
        id: "design",
        content: <DesignPanel isReadOnly={readOnly} canRun={allRequiredLayersUploaded} />,
        isComplete: solveWorkflow === "COMPLETE",
        isLoading: isSolveInProgress,
      },
    ],
    [allRequiredLayersUploaded, dataPrepWorkflow, hasInProgressLayers, inputLayers, isDataPrepInProgress, isSolveInProgress, readOnly, solveWorkflow]
  );

  if (!project) return null;

  return (
    <Box data-testid="isg-design-panel" sx={{ height: "100%" }}>
      {!permissionCheck(project.Permission?.Level, Actions.PROJECT_EDIT) ? (
        <NonIdealState size="small" icon={<Lock />} title="View only access" description="You only have view access to this project" />
      ) : (
        <StackedNavigation rootComponent={<AccordionStepper panes={accordionPanes} id={project.ID} />} screens={isgStackedNavigationPanels} />
      )}
    </Box>
  );
};

export default Panel;
